import { CreatTaskDto } from '@/app/Data/DTO/TaskDto';
import { Localizations } from '@/app/Utilities/localization';
import { SnackbarService } from '@/app/Utilities/snackbar/snackbar.service';
import { MentionMember } from '@/app/core/Models/Mention';
import { TaskDetail } from '@/app/core/Models/Task';
import { TeamMember } from '@/app/core/Models/TeamMember';
import { Attachment, FileModel, FileType } from '@/app/core/Models/file';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { combineLatest, filter, first, firstValueFrom, map, startWith } from 'rxjs';
import { SubSink } from 'subsink';
import { LocalizationViewModel } from '../../ViewModels/localizationViewModel';
import { TaskViewModel } from '../../ViewModels/taskViewModel';
import { UsersViewModel } from '../../ViewModels/usersViewModel';
import momento from '@/app/Utilities/momento';
import { MatMenuTrigger } from '@angular/material/menu';
import dayjs from 'dayjs';
import { Tag } from '@/app/core/Models/tag';
import { User } from '@/app/core/Models/User';

enum ReminderType {
  none = 'none',
  in_15_minutes = 'in_15_minutes',
  in_30_minutes = 'in_30_minutes',
  in_one_day = 'in_one_day',
  in_two_days = 'in_two_days',
  in_one_week = 'in_one_week',
  custom = 'custom',
}

@Component({
  templateUrl: './task-form.component.html',
  styleUrls: ['./task-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskFormComponent implements OnInit {
  @ViewChild('calendarTrigger') calendarTrigger: MatMenuTrigger;
  taskForm: FormGroup;
  loadingTask = false;
  savingTask = false;
  editMode = false;
  linkToContact = !!this.data?.channel_id;
  subSink = new SubSink();
  taskDetail$ = this.taskViewModel.task$;
  mentions$ = this.usersViewModel.mentions$
  localization = this.localiztionViewModel.localization
  limitSize = 26214400;
  mentionedMember: MentionMember | undefined;
  attachments: Attachment[] = []
  reminder = new FormControl(ReminderType.none)
  currentDate = new Date()
  minimumTime: string
  reminderDate!: Date
  reminderTime!: string
  todayDate: Date = new Date();

  userSearchControl = new FormControl();
  usersList$ = combineLatest([
    this.usersViewModel.usersList$,
    this.userSearchControl.valueChanges.pipe(startWith(''), filter(x => typeof x === 'string'), map((search: string) => search.toLowerCase())),
  ]).pipe(map(([journeys, search]) => journeys.filter(user => `${user.firstName}${user.lastName}`.toLowerCase().includes(search))));

  constructor(
    private localiztionViewModel: LocalizationViewModel,
    private snackBarService: SnackbarService,
    private taskViewModel: TaskViewModel,
    private usersViewModel: UsersViewModel,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<TaskFormComponent>,
    private elementRef: ElementRef,
    @Inject(MAT_DIALOG_DATA) public data: {
      taskId?: number;
      channel_id?: number,
      inbox_id?: number,
      yobi_crm_company_id?: any,
      contact_id?: any,
      status?: string
    }) {
    this.initDate()
    this.initForm();
  }

  get attachmentsForm(): FormArray {
    return this.taskForm.get('attachments') as FormArray;
  }

  get deletedAttachments(): FormArray {
    return this.taskForm.get('deleted') as FormArray;
  }

  ngOnInit(): void {
    this.subSink.sink = this.reminder.valueChanges.subscribe(value => {
      this.taskForm.get('reminder_value')?.patchValue(value);
    });
    this.subSink.sink = this.attachmentsForm.valueChanges.subscribe((files: { file_url: string, filename: string, type: FileType }[]) => {
      this.attachments = files.map(({ file_url, filename, type }) => ({ url: file_url, name: filename, type }))
      this.cdr.detectChanges()
    });
    this.initTask();
  }

  initDate() {
    const offset = 30 - (momento(this.currentDate).date.getMinutes() % 30);
    this.minimumTime = momento(this.currentDate).add(offset, "minute").format('HH:mm')
    this.reminderTime = this.minimumTime
    this.reminderDate = this.currentDate
  }

  initForm(): void {
    this.taskForm = this.fb.group({
      title: new FormControl('', [Validators.required]),
      description: new FormControl(''),
      channel_id: new FormControl(this.data?.channel_id),
      attachments: this.fb.array([]),
      deleted: this.fb.array([]),
      inbox_id: new FormControl(this.data?.inbox_id ?? ''),
      deadline: new FormControl(),
      deadlineViewModel: new FormControl(),
      tags: new FormControl([]),
      tagsModel: new FormControl([]),
      task_priority: new FormControl(),
      status: new FormControl('Open'),
      assignees: new FormControl([], [Validators.required]),
      assignedUserViewModel: new FormControl([], [Validators.required]),
      reminder_value: new FormControl('none'),
    });
    if (this.data?.status) {
      this.taskForm.get('status')?.patchValue(this.convertStatusToUIFormat(this.data?.status))
    }
  }

  initTask() {
    if (this.data?.taskId) {
      this.loadingTask = true;
      this.editMode = true;
      this.taskViewModel.loadTaskDetail(this.data.taskId);
      this.subSink.sink = this.taskDetail$.pipe(
        first((task): task is TaskDetail => task?.task_id == this.data?.taskId),
      ).subscribe(async ({ assigned, description, reminder, body, title, inbox_id, attachments, status }) => {
        this.loadingTask = false
        if (assigned) {
          this.mentionedMember = {
            id: assigned.user_id,
            name: `${assigned.firstName} ${assigned.lastName}`,
            img: assigned.avatar ?? '',
            active: false
          }
        }
        if (reminder) {
          this.reminder.patchValue(ReminderType.custom)
          this.taskForm.get('assigned_to')!.addValidators(Validators.required)
          this.reminderDate = new Date(reminder * 1000)
          this.reminderTime = momento(this.reminderDate).format('HH:mm')
        }
        if (status) {
          this.taskForm.get('status')?.patchValue(this.convertStatusToUIFormat(status));
        }
        if (description) {
          const members = await firstValueFrom(this.mentions$)
          description = !body
            ? description
            : (body ?? []).map(item => {
              switch (item.type) {
                case 'text':
                  return item.text
                case 'mention':
                  const member = members.find(m => m.id === item.id)
                  return member ? `<@mention_${item.id}>` : item.user.replace(/\s+/ig, '_')
                default:
                  return ''
              }
            }).join('')
          members.forEach(member => {
            description = description.replace(new RegExp(`<@mention_${member.id}>`, 'ig'), `@${member.name}`)
          })
        }
        this.taskForm.patchValue({ title, description, assigned_to: assigned?.user_id, inbox_id })
        attachments.forEach(({ attachment_id, attachment_url, filename }) => {
          this.attachmentsForm.push(
            this.fb.group({ filename, attachment_id, file_url: attachment_url })
          );
        });
        this.cdr.detectChanges();
      });
    }
  }

  onFiles(files: FileModel[]) {
    files.forEach(attachment => this.addAttachment(attachment));
  }

  addAttachment(attachment: FileModel) {
    if (this.attachments.length >= 3) {
      this.snackBarService.openAlert({
        message: Localizations.alert_dialog.max_attachments,
        translateParams: { max: '3' },
        type: 'info',
      });
      return
    }
    this.attachmentsForm.push(
      this.fb.group({
        type: attachment.type,
        file_url: attachment.url,
        filename: attachment.name,
        file: attachment.file
      })
    );
  }

  removeAttachment(index: number) {
    const attachment = this.attachmentsForm.at(index);
    const file_id = attachment.get('attachment_id')?.value
    file_id && this.deletedAttachments.push(this.fb.group({ file_id }));
    this.attachmentsForm.removeAt(index);
  }

  _onDateChange(event: string) {
    this.reminderDate = new Date(event)
    this.cdr.detectChanges();
  };

  _onTeamMemberSelect(member: TeamMember): void {
    if (member?.user_id) {
      this.taskForm?.get('assigned_to')?.setValue(member?.user_id);
      this.mentionedMember = {
        id: member.user_id,
        name: `${member.given_name} ${member.family_name}`,
        img: member.avatar ?? '',
        active: false
      }
    }
    this.cdr.detectChanges();
  }

  _toggleTaskLink() {
    this.linkToContact = !this.linkToContact
    this.cdr.detectChanges();
  }

  async submit() {
    this.taskForm.markAllAsTouched();
    if (!this.taskForm.valid || this.savingTask || this.reminder.value !== ReminderType.none && !this.taskForm.get('assignees')!.value.length) {
      this.reminder.value !== ReminderType.none && !this.taskForm.get('assignees')!.value.length && this.taskForm.get('assignees')?.setErrors({ required: true })
      return;
    }
    let request: CreatTaskDto = {
      task: {
        ...this.taskForm.value,
        channel_id: this.linkToContact
          ? this.data?.channel_id
          : undefined,
      },
      taskId: this.data?.taskId,
    }

    // Convert status to backend format
    if (request.task.status) {
      request.task.status = this.convertStatusToBackendFormat(request.task.status);
    }

    if (request.task.description) {
      let members = await firstValueFrom(this.mentions$)
      members.forEach(member => {
        request.task.description = request.task.description.replace(new RegExp(`@${member.name}`, 'ig'), `<@mention_${member.id}>`)
      })
    }
    request.task.attachments = request.task.attachments.filter(a => !(a as any).attachment_id)
    this.currentDate = new Date()
    const reminder =
      this.reminder.value === 'in_15_minutes' ? momento(this.currentDate).add(15, 'minute') :
        this.reminder.value === 'in_30_minutes' ? momento(this.currentDate).add(30, 'minute') :
          this.reminder.value === 'in_one_week' ? momento(this.currentDate).add(1, 'week') :
            this.reminder.value === 'in_one_day' ? momento(this.currentDate).add(1, 'day') :
              this.reminder.value === 'in_two_days' ? momento(this.currentDate).add(2, 'day') :
                this.reminder.value === 'custom' ? momento(this.reminderDate)
                  .set(momento(this.reminderTime, 'HH:mm').date.getHours(), 'hour')
                  .set(momento(this.reminderTime, 'HH:mm').date.getMinutes(), 'minute')
                  : undefined
    if (reminder && reminder.isBefore(momento(this.currentDate).date)) {
      this.snackBarService.openAlert({
        message: 'Invalid reminder date',
        type: 'failure',
      });
      return
    } else if (reminder) {
      request.task.reminder = reminder.unix()
    }

    this.savingTask = true;
    const formValues: any = request.task;
    Object.keys(formValues).forEach((key: string) => {
      if (formValues[key] === undefined || key === 'tagsModel' || key === 'deadlineViewModel' || key === 'assignedUserViewModel' || formValues[key] === null || formValues[key] === '' || (typeof formValues[key] === 'object' && !formValues[key].length)) {
        delete formValues[key];
      }
    });
    request = { ...request, task: formValues };
    this.editMode
      ? this.updateTask(request)
      : this.createTask(request)
  }


  uploadTaskAttachments(attachments: Array<{ type: string, file_url: string, filename: string, file?: any }>): Promise<{ state: boolean, result: Array<{ filename: string, type: string, url: string }> }> {
    let _attachments: FormData | undefined = this.taskViewModel.getBodyAttachment(attachments);
    return new Promise(async (resolve) => {
      let uploadedAttachments = await firstValueFrom(this.taskViewModel.uploadAttachments(_attachments as FormData));
      resolve({ state: true, result: uploadedAttachments as any });
    });
  }

  async createTask(request: CreatTaskDto) {
    if (this.data && this.data.yobi_crm_company_id) request = { ...request, task: { ...request.task, yobi_crm_company_id: this.data.yobi_crm_company_id } };
    if (this.data && this.data.contact_id) request = { ...request, task: { ...request.task, contact_id: this.data.contact_id } };
    let uploadTaskAttachments: { state: boolean, result: Array<{ filename: string, type: string, url: string }> };
    if (request.task && request.task.attachments && request.task.attachments.length) {
      uploadTaskAttachments = await this.uploadTaskAttachments(request.task.attachments);
      request = { ...request, task: { ...request.task, attachments: uploadTaskAttachments.result as any } };
    };
    this.taskViewModel.createTask(request).subscribe({
      next: (result) => {
        this.reset();
        this.close({ type: 'created', data: result });
        this.snackBarService.openAlert({
          message: Localizations.alert_dialog.task_created,
          type: 'success'
        })
      },
      error: () => {
        this.savingTask = false;
        this.snackBarService.openAlert({
          message: Localizations.alert_dialog.task_not_created,
          type: 'failure',
        });
      }
    });
  }

  async updateTask(request: CreatTaskDto) {
    if (this.data && this.data.yobi_crm_company_id) request = { ...request, task: { ...request.task, yobi_crm_company_id: this.data.yobi_crm_company_id } };
    if (this.data && this.data.contact_id) request = { ...request, task: { ...request.task, contact_id: this.data.contact_id } };
    let uploadTaskAttachments: { state: boolean, result: Array<{ filename: string, type: string, url: string }> };
    if (request.task && request.task.attachments && request.task.attachments.length) {
      uploadTaskAttachments = await this.uploadTaskAttachments(request.task.attachments);
      request = { ...request, task: { ...request.task, attachments: uploadTaskAttachments.result as any } };
    };
    this.taskViewModel.editTask(request).subscribe({
      next: (res) => {
        this.savingTask = false;
        this.close({ type: 'updated', data: res });
        this.snackBarService.openAlert({
          message: Localizations.alert_dialog.task_updated,
          type: 'success'
        })
      },
      error: () => {
        this.savingTask = false;
        this.snackBarService.openAlert({
          message: Localizations.alert_dialog.task_not_updated,
          type: 'failure',
        });
      }
    });
  }

  reset() {
    this.mentionedMember = undefined
    this.savingTask = false;
    this.taskForm.reset();
    this.attachmentsForm.clear();
    this.deletedAttachments.clear();
    this.cdr.detectChanges();
  }

  close(data?: { type: 'archive' | 'updated' | 'created', data?: TaskDetail }) {
    this.dialogRef.close(data);
  }

  archive() {
    this.close({ type: 'archive' });
  }

  onSelectDate(date: Date) {
    const timestamp = dayjs(date).unix();
    this.deadlineViewModel?.patchValue(dayjs.unix(timestamp).format('MM/DD/YYYY'));
    this.deadline?.patchValue(timestamp);
  }

  closeDatePicker() {
    this.calendarTrigger.closeMenu();
  }

  onGetTagOnTask(event: { data: Tag, action: 'add' | 'delete' }): void {
    let selectedTags: Tag[] = this.tagsModel?.value;
    let tagIds = [];
    if (event.action === 'add') {
      let selected = selectedTags.filter((tag: Tag) => tag.tag_id === event.data.tag_id);
      !selected.length && selectedTags.push(event.data);
    } else {
      selectedTags = selectedTags.filter((tag: Tag) => tag.tag_id !== event.data.tag_id);
    }
    tagIds = selectedTags.map((tag: Tag) => tag.tag_id);
    this.tags?.patchValue(tagIds);
    this.tagsModel?.patchValue(selectedTags);
  }

  onChangePriority(event: number): void {
    const stash: string = event.toString();
    const values: any = { '4': 'High', '3': 'Medium', '2': 'Low', '1': 'None' };
    this.task_priority?.patchValue(values[stash]);
  }

  onChangeStatus(event: 'Open' | 'In Progress' | 'Done' | 'Trash'): void {
    this.status?.patchValue(event);
  }

  selectAssignees(user: User): void {
    let selectedAssignees = [...this.assignedUserViewModel?.value];
    const existing = selectedAssignees.filter((selected: User) => selected.user_id === user.user_id);
    if (!existing.length) {
      selectedAssignees.unshift({ avatar: user.avatar, family_name: user.family_name, give_name: user.given_name, user_id: user.user_id });
    } else {
      selectedAssignees = selectedAssignees.filter((item: User) => item.user_id !== user.user_id);
    }
    this.assignedUserViewModel?.patchValue(selectedAssignees);
    this.assignees?.patchValue(selectedAssignees.map(item => item.user_id));
  }

  isSelected(user_id: number): boolean {
    const selectedAssignees = this.assignedUserViewModel?.value;
    if (selectedAssignees.length) {
      const existing = selectedAssignees.filter((selected: User) => selected.user_id === user_id);
      return existing.length ? true : false;
    } else {
      return false;
    }
  }

  get tags() {
    return this.taskForm.get('tags');
  }

  get tagsModel() {
    return this.taskForm.get('tagsModel');
  }

  get deadline() {
    return this.taskForm.get('deadline');
  }

  get deadlineViewModel() {
    return this.taskForm.get('deadlineViewModel');
  }

  get task_priority() {
    return this.taskForm.get('task_priority');
  }

  get status() {
    return this.taskForm.get('status');
  }

  get statusViewModel() {
    return this.taskForm.get('statusViewModel');
  }

  get assignees() {
    return this.taskForm.get('assignees');
  }

  get assignedUserViewModel() {
    return this.taskForm.get('assignedUserViewModel');
  }

  // Method to determine if we should show the actual date instead of "Today"
  shouldShowActualDate(): boolean {
    return !!this.deadline?.value;
  }

  // Convert backend status format to UI format
  convertStatusToUIFormat(status: string): string {
    switch (status) {
      case 'open': return 'Open';
      case 'in-progress': return 'In Progress';
      case 'done': return 'Done';
      case 'trash': return 'Trash';
      default: return status; // If it's already in the correct format
    }
  }

  // Convert UI status format to backend format
  convertStatusToBackendFormat(status: string): string {
    switch (status) {
      case 'Open': return 'open';
      case 'In Progress': return 'in-progress';
      case 'Done': return 'done';
      case 'Trash': return 'trash';
      default: return status; // If it's already in the correct format
    }
  }
}
